/**
 * app/screens/ItemList.tsx
 * -------------------------
 * What: Lists items with basic filtering/sorting and navigation to item detail.
 * Why: Primary inventory view for managing a user's items.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  Modal,
  Animated,
  useWindowDimensions,
  Platform,
  Pressable,
  SafeAreaView,
  StatusBar,
  Image,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, CommonActions, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import { Ionicons } from '@expo/vector-icons';
import { useSubscription } from '../../lib/SubscriptionContext';
import { usePaginatedItems } from '../../lib/DataContext';
import { useTheme } from '../../lib/ThemeContext';
import { useImageCache } from '../../lib/ImageCacheContext';
import ReactNative from 'react-native';
import { useData } from '../../lib/DataContext';
import FormTextInput from '../components/FormTextInput';
import ThemedButton from '../components/ThemedButton';

const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
  });
};

const toSentenceCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

type Item = {
  id: string;
  name: string;
  description?: string;
  make?: string;
  model?: string;
  room_id: string;
  purchase_date?: string;
  price?: number;
  created_at: string;
  rooms: {
    name: string;
  };
  item_images?: {
    id: string;
    url: string;
    order?: number;
    updated_at?: string;
  }[];
};

type Room = {
  id: string;
  name: string;
};

type ItemListScreenNavigationProp = StackNavigationProp<RootStackParamList>;
type ItemListScreenRouteProp = RouteProp<RootStackParamList, 'ItemsTab'>;
type SortOption = 'recent' | 'name';

// Debounce hook
function useDebouncedValue(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// Move isSmallScreen check outside component
const useIsSmallScreen = () => {
  const { width } = useWindowDimensions();
  return width < 768;
};

// Create a function to generate styles based on screen size
const createStyles = (isSmallScreen: boolean) => StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
    borderWidth: 1,
    ...(Platform.OS === 'web' && !isSmallScreen ? {
      width: '100%',
    } : {
      flex: 1,
      overflow: 'hidden',
    }),
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    marginLeft: 8,
    paddingRight: 32,
    height: '100%',
    ...(Platform.OS === 'web' && {
      borderWidth: 0,
      outlineWidth: 0,
      outlineStyle: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
    }),
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: 0,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  addButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  itemGrid: {
    flexDirection: 'column',
    gap: 12,
  },
  itemCard: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
    ...(Platform.OS === 'web' 
      ? { 
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
        }
      : {
          elevation: 2,
          shadowColor: '#000',
          shadowOpacity: 0.06,
          shadowRadius: 6,
          shadowOffset: { width: 0, height: 2 },
        }
    ),
  },
  itemCardMobile: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    marginBottom: 0,
    ...(Platform.OS === 'web' 
      ? { 
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
        }
      : {
          elevation: 2,
          shadowColor: '#000',
          shadowOpacity: 0.06,
          shadowRadius: 6,
          shadowOffset: { width: 0, height: 2 },
        }
    ),
  },
  itemThumbnail: {
    width: 120,
    height: 120,
    borderRadius: 12,
  },
  itemDetailsRedesign: {
    flex: 1,
    paddingHorizontal: 10,
    justifyContent: 'center',
    minWidth: 0,
  },
  itemNameRedesign: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 4,
  },
  itemDetailLabel: {
    fontSize: 13,
    color: '#666',
    marginBottom: 2,
  },
  itemDetailKey: {
    fontWeight: '600',
    color: '#888',
  },
  itemActionsRedesign: {
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    alignSelf: 'stretch',
  },
  actionButtonRedesign: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    marginLeft: 0,
    ...(Platform.OS === 'web' 
      ? { boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)' }
      : {
          elevation: 1,
          shadowColor: '#000',
          shadowOpacity: 0.04,
          shadowRadius: 2,
          shadowOffset: { width: 0, height: 1 },
        }
    ),
  },
  placeholderContainer: {
    width: 136,
    height: 136,
    backgroundColor: '#2a2a2a',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#333',
    gap: 4,
    padding: 12,
  },
  placeholderText: {
    color: '#666',
    fontSize: 11,
    fontWeight: '500',
  },
  paginationContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    width: '100%',
  },
  paginationContainerTop: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  paginationButton: {
    padding: 8,
    borderRadius: 8,
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  emptyText: {
    fontSize: 16,
    marginBottom: 16,
  },
  addFirstButton: {
    borderRadius: 10,
    padding: 16,
  },
  addFirstButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  toast: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    zIndex: 1000,
  },
  toastText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteModalContent: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  deleteModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  deleteModalText: {
    fontSize: 16,
    color: '#fff',
    marginBottom: 20,
    textAlign: 'center',
  },
  deleteModalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
  },
  deleteModalButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  deleteButton: {
    backgroundColor: '#ff3b30',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
  },
  metaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 4,
    backgroundColor: '#333',
  },
  loadMoreButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  loadMoreButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  pageNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default function ItemList() {
  const navigation = useNavigation<ItemListScreenNavigationProp>();
  const route = useRoute<ItemListScreenRouteProp>();
  const { width } = useWindowDimensions();
  const isSmallScreen = useIsSmallScreen();
  const { theme, themeType } = useTheme();
  // Consistent divider style for modal separators
  const dividerStyle = { height: 1, backgroundColor: theme.border, alignSelf: 'stretch', marginVertical: 16 } as const;
  const { user } = useAuth();
  const { tier, itemLimit, currency, dateFormat } = useSubscription();
  const { getCachedUrl, setCachedUrl, setCachedObjectUrl, clearItemCache } = useImageCache();
  const {
    items,
    isLoading,
    hasMore,
    search,
    setSearch,
    filterRoom,
    setFilterRoom,
    sortBy,
    setSortBy,
    totalCount,
    page,
    PAGE_SIZE,
    refresh,
    changePage
  } = usePaginatedItems();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 600);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const scrollViewRef = React.useRef<ScrollView>(null);
  const CARD_MAX_WIDTH = 800;
  const [signedThumbUrls, setSignedThumbUrls] = useState<Record<string, string>>({});
  const [updatingImages, setUpdatingImages] = useState<Set<string>>(new Set());
  const [preloadingImages, setPreloadingImages] = useState<Set<string>>(new Set());
  const { rooms } = useData();
  const [selectedRoomFilter, setSelectedRoomFilter] = useState<string | null>(filterRoom);
  const [showRoomPickerModal, setShowRoomPickerModal] = useState(false);
  // Outer scroll container (web desktop) – used to jump to top on pagination
  const wrapperScrollRef = React.useRef<HTMLDivElement>(null);

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  // Helper: on RN-web returns the DOM node inside ScrollView (mobile web)
  const getWebScroller = () => {
    if (Platform.OS !== 'web') return null;
    // @ts-ignore – RN-web exposes this
    return scrollViewRef.current?.getScrollableNode?.() as HTMLElement | null;
  };

  // Unified scroll-to-top used by pagination buttons **and** automatic effect
  const scrollToTop = () => {
    try {
      // 1) Browser window – instant jump (harmless on native)
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0 });
      }

      // 2) Desktop web wrapper div
      if (Platform.OS === 'web' && wrapperScrollRef.current) {
        wrapperScrollRef.current.scrollTo({ top: 0, left: 0 });
      }

      // 3) RN-web mobile internal node
      const domNode = getWebScroller();
      domNode?.scrollTo({ top: 0, left: 0 });

      // 4) Native ScrollView (animated)
      scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
    } catch (err) {
      console.error('scrollToTop failed', err);
    }
  };

  // ===================  Scroll on page change  ===================
  const didFirstPageEffect = React.useRef(false);
  useEffect(() => {
    if (!didFirstPageEffect.current) {
      didFirstPageEffect.current = true; // skip very first mount
      return;
    }
    scrollToTop();
  }, [page]);

  console.log('📦 [ItemList] Component rendered with state:', {
    itemsCount: items.length,
    isLoading,
    hasMore,
    page,
    search,
    filterRoom,
    sortBy,
    totalCount,
    isFocused
  });

  // Sync search input with debounced search
  useEffect(() => {
    console.log('🔄 [ItemList] Search input changed:', searchInput);
    setSearch(searchInput);
  }, [debouncedSearch, setSearch]);

  // Sync search input with search state
  useEffect(() => {
    console.log('🔄 [ItemList] Search state changed:', search);
    setSearchInput(search);
  }, [search]);

  // Focus effect
  useFocusEffect(
    useCallback(() => {
      console.log('👁️ [ItemList] Screen focused');
      setIsFocused(true);
      return () => {
        console.log('👁️ [ItemList] Screen unfocused');
        setIsFocused(false);
      };
    }, [])
  );

  /* ------------------------------------------------------------------
     Persist page number across unmount/mount cycles (e.g., browser tab
     visibility changes). We store the current page in route params and, on
     first mount, read it back and restore using changePage().
  ------------------------------------------------------------------ */

  // 1) On first mount, if route.params.page exists and differs from our
  //    current page state, jump to that page.
  useEffect(() => {
    if (route.params?.page && route.params.page !== page) {
      console.log('🔄 [ItemList] Restoring page from route params:', route.params.page);
      changePage(route.params.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // 2) Whenever page changes, write it back to route params so it survives
  //    a remount.
  useEffect(() => {
    navigation.setParams({ page });
  }, [page]);

  // Handle explicit refresh requests passed via navigation params (e.g., after Add/Edit screens)
  useEffect(() => {
    if (route.params?.refresh) {
      console.log('🔄 [ItemList] Received explicit refresh request via route params');

      // If a target page was supplied (e.g., returning to same page after edit) respect it,
      // otherwise default to page 1 so newly-added items (sorted by recent) are visible.
      const targetPage = route.params?.page ?? 1;

      if (page !== targetPage) {
        console.log('📄 [ItemList] Changing page from', page, 'to', targetPage);
        changePage(targetPage);
      } else {
        // If we stay on the same page, still trigger data refresh
        refresh();
      }

      // Clear the param so we don't refresh again on minor focus changes
      navigation.setParams({ refresh: undefined });
    }
  }, [route.params?.refresh]);

  // Calculate total pages
  const totalPages = totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  // Replace deprecated shadow* props with boxShadow
  const shadowStyle = Platform.OS === 'web' 
    ? { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' } 
    : { 
        elevation: 2,
        shadowColor: '#000', 
        shadowOpacity: 0.25, 
        shadowRadius: 3.84, 
        shadowOffset: { width: 0, height: 2 } 
      };

  const styles = React.useMemo(() => createStyles(isSmallScreen), [isSmallScreen]);

  useEffect(() => {
    if (route.params?.showSuccess && route.params?.message) {
      setToastMessage(route.params.message);
      setShowToast(true);
      Animated.sequence([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.delay(2000),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start(() => {
        setShowToast(false);
        navigation.setParams({ showSuccess: undefined, message: undefined });
      });
    }
  }, [route.params?.showSuccess, route.params?.message]);

  const handleAddItem = () => {
    navigation.navigate('AddItem', { roomId: undefined });
  };

  const handleEditItem = (itemId: string) => {
    navigation.dispatch(
      CommonActions.navigate({
        name: 'EditItem',
        params: { 
          itemId,
          returnPage: page // Pass current page to return to
        }
      })
    );
  };

  const handleDeleteItem = (itemId: string) => {
    setItemToDelete(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      // 1) Fetch the receipt_id before deleting the item
      const { data: itemRow, error: fetchErr } = await supabase
        .from('items')
        .select('receipt_id')
        .eq('id', itemToDelete)
        .single();

      if (fetchErr) throw fetchErr;

      const receiptId = itemRow?.receipt_id as string | null;

      // 2) Delete the item row itself
      const { error: deleteErr } = await supabase
        .from('items')
        .delete()
        .eq('id', itemToDelete);

      if (deleteErr) throw deleteErr;

      // 3) If the item had a receipt, check whether it is still used elsewhere
      if (receiptId) {
        const { count, error: cntErr } = await supabase
          .from('items')
          .select('id', { head: true, count: 'exact' })
          .eq('receipt_id', receiptId);

        if (cntErr) throw cntErr;

        if (count === 0) {
          // Fetch file_path for deletion
          const { data: rec, error: recErr } = await supabase
            .from('receipts')
            .select('file_path')
            .eq('id', receiptId)
            .single();

          if (recErr) throw recErr;

          if (rec?.file_path) {
            await supabase.storage.from('item-documents').remove([rec.file_path]);
          }

          // Finally remove the receipt row
          await supabase.from('receipts').delete().eq('id', receiptId);
        }
      }

      // Refresh the item list instantly
      refresh();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      Alert.alert('Error', 'Failed to delete item');
    }
  };

  const handleSortSelect = (option: SortOption) => {
    setSortBy(option);
    setShowFilterModal(false);
  };

  const handleFilterSelect = (roomId: string | null) => {
    setFilterRoom(roomId);
    setShowFilterModal(false);
  };

  const handleItemPress = (itemId: string) => {
    navigation.navigate('ViewItem', { itemId, returnPage: page });
  };

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return date.toLocaleDateString('en-GB');
      case 'MM/DD/YYYY':
        return date.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      default:
        return date.toLocaleDateString('en-GB');
    }
  };

  // Currency symbols
  const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: 'C$',
    AUD: 'A$',
  };

  // Preload an image to force React Native to load it
  const preloadImage = useCallback(async (url: string, itemId: string) => {
    console.log('🔄 [ItemList] Preloading image for item:', itemId, 'URL:', url);
    setPreloadingImages(prev => new Set(prev).add(itemId));
    
    try {
      await Image.prefetch(url);
      console.log('✅ [ItemList] Image preloaded successfully for item:', itemId);
    } catch (error) {
      console.log('❌ [ItemList] Image preload failed for item:', itemId, error);
    } finally {
      setPreloadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  }, []);

  // Generate signed URLs for thumbnails
  const generateThumbUrls = useCallback(async () => {
    console.log('🖼️ [ItemList] generateThumbUrls called with', items.length, 'items');
    console.log('🖼️ [ItemList] Items data:', items.map(item => ({
      id: item.id,
      name: item.name,
      images: item.item_images?.map(img => ({ id: img.id, url: img.url, order: img.order }))
    })));
    
    if (!items || items.length === 0) return;

    const urlMap: Record<string, string> = {};
    const pathsToSign: string[] = [];
    const meta: Record<string, { itemId: string; cacheKey: string; bustStr: string }> = {};

    // 1) Determine which images need a fresh signed URL
    items.forEach((item) => {
      if (!item.item_images || item.item_images.length === 0) return;

      const sortedImages = [...item.item_images].sort((a, b) => (a.order || 0) - (b.order || 0));
      const firstImage = sortedImages[0];

      if (!firstImage?.url) return;

      // Use a stable cache-buster that only changes when the first image or its order changes.
      // We deliberately avoid updated_at to prevent unnecessary cache misses when paging.
      const bustStr = `${firstImage.id}-${firstImage.order ?? 0}`;
      const cacheKey = `item-${item.id}-thumb-${bustStr}`;

      const cached = getCachedUrl(cacheKey);
      if (cached) {
        urlMap[item.id] = cached;
        return;
      }

      // Needs signing
      pathsToSign.push(firstImage.url);
      meta[firstImage.url] = { itemId: item.id, cacheKey, bustStr };
    });

    // 2) If every thumbnail was cached, we can early-exit
    if (pathsToSign.length === 0) {
      setSignedThumbUrls(urlMap);
      return;
    }

    // 3) Batch-sign the remaining paths (single round-trip)
    console.log('🔗 [ItemList] createSignedUrls for', pathsToSign.length, 'paths');
    const { data, error } = await supabase.storage
      .from('item-images')
      .createSignedUrls(pathsToSign, 60 * 60);

    if (error) {
      console.error('❌ [ItemList] Error from createSignedUrls', error);
    }

    (data || []).forEach((signed) => {
      if (!signed.signedUrl || !signed.path) return;
      const path = signed.path;
      if (!meta[path]) return; // safety guard
      const { itemId, cacheKey, bustStr } = meta[path];
      const sep = signed.signedUrl.includes('?') ? '&' : '?';
      const signedUrlWithBuster = `${signed.signedUrl}${sep}_cb=${bustStr}`;

      urlMap[itemId] = signedUrlWithBuster;
      setCachedUrl(cacheKey, signedUrlWithBuster);
      preloadImage(signedUrlWithBuster, itemId);
    });

    // 4) Update state with all resolved URLs (cached + newly signed)
    console.log('🖼️ [ItemList] Thumb URL map complete for', Object.keys(urlMap).length, 'items');
    setSignedThumbUrls(urlMap);
  }, [items, getCachedUrl, setCachedUrl]);

  // Generate thumbnails when items change
  useEffect(() => {
    console.log('🔄 [ItemList] Items changed, regenerating thumbnails. Items count:', items.length);
    generateThumbUrls();
  }, [generateThumbUrls]);

  // Handle edited item - clear cache and regenerate thumbnails for the edited item
  useEffect(() => {
    if (route.params?.editedItemId) {
      console.log('🔄 [ItemList] Item was edited, clearing cache for item:', route.params.editedItemId);
      console.log('🔄 [ItemList] Current items before cache clear:', items.map(item => ({
        id: item.id,
        name: item.name,
        images: item.item_images?.map(img => ({ id: img.id, order: img.order }))
      })));
      
      // Set the item as updating to force Image component remount
      if (route.params.editedItemId) {
        setUpdatingImages(prev => new Set(prev).add(route.params.editedItemId!));
      }
      
      if (route.params.editedItemId) {
        clearItemCache(route.params.editedItemId);
      }
      
      // Force regenerate thumbnails
      console.log('🔄 [ItemList] Forcing thumbnail regeneration after cache clear');
      generateThumbUrls();
      
      // Clear the updating state after a short delay to show the new image
      setTimeout(() => {
        if (route.params.editedItemId) {
          setUpdatingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(route.params.editedItemId!);
            return newSet;
          });
        }
      }, 100);
      
      // Clear the parameter to prevent repeated processing
      navigation.setParams({ editedItemId: undefined });
    }
  }, [route.params?.editedItemId, clearItemCache, generateThumbUrls, navigation, items]);

  // Whenever the modal is opened, sync the selection with current filter
  useEffect(() => {
    if (showFilterModal) {
      setSelectedRoomFilter(filterRoom);
    }
  }, [showFilterModal, filterRoom]);

  // Room picker modal renderer (same styling as EditItem)
  const renderRoomPickerModal = () => (
    <Modal
      visible={showRoomPickerModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowRoomPickerModal(false)}
    >
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ backgroundColor: theme.card, borderRadius: 20, padding: 24, maxHeight: '80%', width: '90%', maxWidth: 400 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500' }}>Select Room</Text>
            <TouchableOpacity
              onPress={() => setShowRoomPickerModal(false)}
              style={{ padding: 6, backgroundColor: theme.inputBackground, borderRadius: 16 }}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            <TouchableOpacity
              style={{ paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.border }}
              onPress={() => {
                setSelectedRoomFilter(null);
                setShowRoomPickerModal(false);
              }}
            >
              <Text style={{ color: selectedRoomFilter === null ? theme.accent : theme.text }}>All Rooms</Text>
            </TouchableOpacity>
            {rooms.map(room => (
              <TouchableOpacity
                key={room.id}
                style={{ paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.border }}
                onPress={() => {
                  setSelectedRoomFilter(room.id);
                  setShowRoomPickerModal(false);
                }}
              >
                <Text style={{ color: selectedRoomFilter === room.id ? theme.accent : theme.text }}>
                  {room.name.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  if (isLoading && page === 1) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={themeType === 'dark' || themeType === 'blueNeon' ? 'light-content' : 'dark-content'} />

      {/* ===== Desktop layout will render the pill later; on mobile we render below (handled in layout section) ===== */}

      {/* Desktop layout */}
      {!isSmallScreen && Platform.OS === 'web' ? (
        <div style={{ scrollbarGutter: 'stable' }} ref={wrapperScrollRef}>
          {/* Desktop header layout */}
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', paddingHorizontal: 16, marginTop: 16, marginBottom: 16 }}>
            <View style={{ paddingLeft: 0, minWidth: 400, maxWidth: 400 }}>
              <View style={[styles.searchContainer, { backgroundColor: theme.inputBackground, marginBottom: 0 }, glowStyle]}> 
                <Ionicons name="search" size={20} color={theme.text} style={styles.searchIcon} />
                <FormTextInput
                  style={[styles.searchInput, { color: theme.text }]}
                  placeholder="Search items..."
                  placeholderTextColor={theme.secondaryText}
                  value={searchInput}
                  onChangeText={setSearchInput}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
                {searchInput.length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      setSearchInput('');
                      setSearch('');
                    }}
                    style={styles.clearButton}
                    accessibilityRole="button"
                    accessibilityLabel="Clear search"
                  >
                    <Ionicons name="close-circle" size={20} color={theme.text} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
            <View style={{ flex: 1 }} />
            <View style={{ flexDirection: 'row', gap: 8, marginRight: 16 }}>
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  { backgroundColor: (sortBy !== 'recent' || filterRoom) ? theme.accent : theme.inputBackground }
                ]}
                onPress={() => setShowFilterModal(true)}
                accessibilityRole="button"
                accessibilityLabel="Filter and sort items"
              >
                <Ionicons name="options-outline" size={24} color={theme.text} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.addButton, { backgroundColor: theme.accent }]}
                onPress={handleAddItem}
                accessibilityRole="button"
                accessibilityLabel="Add new item"
              >
                <Ionicons name="add" size={24} color={theme.buttonText} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%' }} />
          <View style={{ height: 16 }} />
          {/* Active room filter pill (desktop) */}
          {filterRoom && (
            <View style={{ paddingHorizontal: 16, marginBottom: 8 }}>
              <ThemedButton
                title={`${rooms.find(r => r.id === filterRoom)?.name ?? 'Room'} ×`}
                onPress={() => handleFilterSelect(null)}
                style={{ alignSelf: 'flex-start' }}
                accessibilityLabel="Clear room filter"
              />
            </View>
          )}
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', paddingHorizontal: 16, marginBottom: 16 }}>
            <Text style={[styles.paginationText, { color: theme.text, textAlign: 'left', paddingLeft: 0 }]}> 
              {(() => {
                if ((totalCount || 0) === 0) return 'No items found';
                const startItem = ((page - 1) * PAGE_SIZE) + 1;
                const endItem = Math.min(page * PAGE_SIZE, totalCount || 0);
                const flags: string[] = [];
                if (filterRoom) flags.push('filtered');
                if (search.trim().length > 0) flags.push('search results');
                let text = `Displaying ${startItem}-${endItem} of ${totalCount || 0} items`;
                if (flags.length) text += ` (${flags.join(', ')})`;
                return text;
              })()}
            </Text>
            <View style={{ flex: 1 }} />
            {/* Desktop pagination */}
            {totalPages > 1 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 16 }}>
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === 1 && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} style={{ marginLeft: -12 }} />
                  </View>
                </TouchableOpacity>
               
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === 1 && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(page - 1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                </TouchableOpacity>
              
                <Text style={[styles.paginationText, { color: theme.text, textAlign: 'right', marginHorizontal: 8 }]}>Page {page} of {totalPages}</Text>
                
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === totalPages && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(page + 1);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.paginationButton,
                    { backgroundColor: theme.accent },
                    page === totalPages && styles.paginationButtonDisabled
                  ]}
                  onPress={() => {
                    changePage(totalPages);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} style={{ marginRight: -12 }} />
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </div>
      ) : (
        /* Mobile layout */
        <View>
          {/* Mobile header */}
          <View style={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 }}>
            {/* Search and buttons row */}
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
              <View style={{ flex: 1 }}>
                <View style={[styles.searchContainer, { backgroundColor: theme.inputBackground, marginBottom: 0 }, glowStyle]}> 
                  <Ionicons name="search" size={20} color={theme.text} style={styles.searchIcon} />
                  <FormTextInput
                    style={[styles.searchInput, { color: theme.text }]}
                    placeholder="Search items..."
                    placeholderTextColor={theme.secondaryText}
                    value={searchInput}
                    onChangeText={setSearchInput}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                  {searchInput.length > 0 && (
                    <TouchableOpacity
                      onPress={() => {
                        setSearchInput('');
                        setSearch('');
                      }}
                      style={styles.clearButton}
                      accessibilityRole="button"
                      accessibilityLabel="Clear search"
                    >
                      <Ionicons name="close-circle" size={20} color={theme.text} />
                    </TouchableOpacity>
                  )}
                </View>
              </View>
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  { backgroundColor: (sortBy !== 'recent' || filterRoom) ? theme.accent : theme.inputBackground }
                ]}
                onPress={() => setShowFilterModal(true)}
                accessibilityRole="button"
                accessibilityLabel="Filter and sort items"
              >
                <Ionicons name="options-outline" size={24} color={theme.text} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.addButton, { backgroundColor: theme.accent }]}
                onPress={handleAddItem}
                accessibilityRole="button"
                accessibilityLabel="Add new item"
              >
                <Ionicons name="add" size={24} color={theme.buttonText} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ height: 1, backgroundColor: theme.border }} />
          {/* Mobile pagination */}
          <View style={{ 
            paddingHorizontal: 16, 
            paddingVertical: 12, 
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%'
          } as const}>
            {filterRoom && (
              <ThemedButton
                title={`${rooms.find(r => r.id === filterRoom)?.name ?? 'Room'} ×`}
                onPress={() => handleFilterSelect(null)}
                style={{ alignSelf: 'center', marginBottom: 6 }}
                accessibilityLabel="Clear room filter"
              />
            )}

            <Text style={[styles.paginationText, { color: theme.text }]}>
              {(() => {
                if ((totalCount || 0) === 0) return 'No items found';
                const startItem = ((page - 1) * PAGE_SIZE) + 1;
                const endItem = Math.min(page * PAGE_SIZE, totalCount || 0);
                const flags2: string[] = [];
                if (filterRoom) flags2.push('filtered');
                if (search.trim().length > 0) flags2.push('search results');
                let text = `Displaying ${startItem}-${endItem} of ${totalCount || 0} items`;
                if (flags2.length) text += ` (${flags2.join(', ')})`;
                return text;
              })()}
            </Text>
            {totalPages > 1 && (
              <View style={{ 
                flexDirection: 'row', 
                alignItems: 'center', 
                gap: 8, 
                marginTop: 8,
                justifyContent: 'center',
                width: '100%'
              } as const}>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === 1 && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                    <Ionicons name="chevron-back" size={20} color={theme.buttonText} style={{ marginLeft: -12 }} />
                  </View>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === 1 && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(page - 1);
                    scrollToTop();
                  }}
                  disabled={page === 1}
                >
                  <Ionicons name="chevron-back" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <Text style={[styles.paginationText, { color: theme.text, marginHorizontal: 8 }]}>
                  Page {page} of {totalPages}
                </Text>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(page + 1);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.paginationButton, { backgroundColor: theme.accent }, page === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => {
                    changePage(totalPages);
                    scrollToTop();
                  }}
                  disabled={page === totalPages}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} style={{ marginRight: -12 }} />
                    <Ionicons name="chevron-forward" size={20} color={theme.buttonText} />
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </View>
          <View style={{ height: 1, backgroundColor: theme.border, marginBottom: 16 }} />
        </View>
      )}
      {/* Cards (with scrollbar-gutter: stable on web) */}
      {Platform.OS === 'web' ? (
        <div style={{ overflowY: 'auto', height: '100%', scrollbarGutter: 'stable' }} ref={wrapperScrollRef}>
          <ScrollView style={{ flex: 1 }} ref={scrollViewRef}>
            <View style={{ paddingHorizontal: 16 }}>
              <View style={[styles.itemGrid, themeType === 'blueNeon' && { paddingTop: 8 }]}>
                {items.map((item) => (
                  <TouchableOpacity
                    key={item.id}
                    style={[
                      styles.itemCard,
                      glowStyle,
                      isSmallScreen && styles.itemCardMobile,
                      { backgroundColor: theme.card, borderColor: theme.border }
                    ]}
                    onPress={() => handleItemPress(item.id)}
                    activeOpacity={0.8}
                  >
                    {/* Item name occupies full width on mobile */}
                    {isSmallScreen && (
                      <Text
                        style={[
                          styles.itemNameRedesign,
                          {
                            color: theme.text,
                            fontSize: 17,
                            textAlign: 'left',
                            alignSelf: 'flex-start',
                            marginBottom: 4,
                          },
                        ]}
                        numberOfLines={2}
                      >
                        {toTitleCase(item.name)}
                      </Text>
                    )}

                    <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                      {item.item_images && item.item_images.length > 0 ? (
                        (updatingImages.has(item.id) || preloadingImages.has(item.id) || !signedThumbUrls[item.id]) ? (
                          <View style={[
                            styles.placeholderContainer,
                            {
                              backgroundColor: theme.inputBackground,
                              borderColor: theme.border,
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: !isSmallScreen ? 24 : 0,
                            },
                          ]}> 
                            <ActivityIndicator size="small" color={theme.accent} />
                          </View>
                        ) : (
                          <View style={[styles.placeholderContainer, {
                            backgroundColor: theme.inputBackground,
                            borderColor: theme.border,
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: !isSmallScreen ? 24 : 0,
                          }]}> 
                            <Image
                              key={`thumb-${item.id}`}
                              source={{ uri: signedThumbUrls[item.id] }}
                              style={[styles.itemThumbnail, { borderRadius: 12 }]}
                              resizeMode="contain"
                            />
                          </View>
                        )
                      ) : (
                        <View style={[styles.placeholderContainer, {
                          backgroundColor: theme.inputBackground,
                          borderColor: theme.border,
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginRight: !isSmallScreen ? 24 : 0,
                        }]}> 
                          <Ionicons name="image-outline" size={36} color={theme.secondaryText} />
                          <Text style={[styles.placeholderText, { color: theme.secondaryText }]}>No Image</Text>
                        </View>
                      )}
                      <View style={[styles.itemDetailsRedesign, { justifyContent: 'center' }]}> 
                        {!isSmallScreen ? (
                          <>
                            <Text style={[styles.itemNameRedesign, { color: theme.text }]} numberOfLines={1}>
                              {toTitleCase(item.name)}
                            </Text>
                            <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                              <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Room:</Text> {item.rooms?.name ? toTitleCase(item.rooms.name) : '-'}
                            </Text>
                            <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                              <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Purchased:</Text> {formatDate(item.purchase_date ?? null)}
                            </Text>
                            <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                              <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Price:</Text> {item.price !== null && item.price !== undefined ? `${currencySymbols[currency] || '$'}${item.price.toFixed(2)}` : '-'}
                            </Text>
                          </>
                        ) : (
                          <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                            {item.rooms?.name ? toTitleCase(item.rooms.name) : '-'}
                          </Text>
                        )}
                      </View>
                      <View style={styles.itemActionsRedesign}>
                        <TouchableOpacity
                          style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]} 
                          onPress={(e) => {
                            e.stopPropagation();
                            handleEditItem(item.id);
                          }}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                          accessibilityLabel={`Edit item ${item.name}`}
                        >
                          <Ionicons name="pencil" size={20} color={theme.accent} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]} 
                          onPress={(e) => {
                            e.stopPropagation();
                            handleDeleteItem(item.id);
                          }}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                          accessibilityLabel={`Delete item ${item.name}`}
                        >
                          <Ionicons name="trash" size={20} color={theme.accent} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>
        </div>
      ) : (
        <ScrollView style={{ flex: 1 }} ref={scrollViewRef}>
          <View style={{ paddingHorizontal: 16 }}>
            <View style={[styles.itemGrid, themeType === 'blueNeon' && { paddingTop: 8 }]}>
              {items.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.itemCard,
                    glowStyle,
                    isSmallScreen && styles.itemCardMobile,
                    { backgroundColor: theme.card, borderColor: theme.border }
                  ]}
                  onPress={() => handleItemPress(item.id)}
                  activeOpacity={0.8}
                >
                  {/* Item name occupies full width on mobile */}
                  {isSmallScreen && (
                    <Text
                      style={[
                        styles.itemNameRedesign,
                        {
                          color: theme.text,
                          fontSize: 17,
                          textAlign: 'left',
                          alignSelf: 'flex-start',
                          marginBottom: 4,
                        },
                      ]}
                      numberOfLines={2}
                    >
                      {toTitleCase(item.name)}
                    </Text>
                  )}

                  <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                    {item.item_images && item.item_images.length > 0 ? (
                      (updatingImages.has(item.id) || preloadingImages.has(item.id) || !signedThumbUrls[item.id]) ? (
                        <View style={[
                          styles.placeholderContainer,
                          {
                            backgroundColor: theme.inputBackground,
                            borderColor: theme.border,
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: !isSmallScreen ? 24 : 0,
                          },
                        ]}> 
                          <ActivityIndicator size="small" color={theme.accent} />
                        </View>
                      ) : (
                        <View style={[styles.placeholderContainer, {
                          backgroundColor: theme.inputBackground,
                          borderColor: theme.border,
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginRight: !isSmallScreen ? 24 : 0,
                        }]}> 
                          <Image
                            key={`thumb-${item.id}`}
                            source={{ uri: signedThumbUrls[item.id] }}
                            style={[styles.itemThumbnail, { borderRadius: 12 }]}
                            resizeMode="contain"
                          />
                        </View>
                      )
                    ) : (
                      <View style={[styles.placeholderContainer, {
                        backgroundColor: theme.inputBackground,
                        borderColor: theme.border,
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: !isSmallScreen ? 24 : 0,
                      }]}> 
                        <Ionicons name="image-outline" size={36} color={theme.secondaryText} />
                        <Text style={[styles.placeholderText, { color: theme.secondaryText }]}>No Image</Text>
                      </View>
                    )}
                    <View style={[styles.itemDetailsRedesign, { justifyContent: 'center' }]}> 
                      {!isSmallScreen ? (
                        <>
                          <Text style={[styles.itemNameRedesign, { color: theme.text }]} numberOfLines={1}>
                            {toTitleCase(item.name)}
                          </Text>
                          <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                            <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Room:</Text> {item.rooms?.name ? toTitleCase(item.rooms.name) : '-'}
                          </Text>
                          <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                            <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Purchased:</Text> {formatDate(item.purchase_date ?? null)}
                          </Text>
                          <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                            <Text style={[styles.itemDetailKey, { color: theme.secondaryText }]}>Price:</Text> {item.price !== null && item.price !== undefined ? `${currencySymbols[currency] || '$'}${item.price.toFixed(2)}` : '-'}
                          </Text>
                        </>
                      ) : (
                        <Text style={[styles.itemDetailLabel, { color: theme.secondaryText }]}> 
                          {item.rooms?.name ? toTitleCase(item.rooms.name) : '-'}
                        </Text>
                      )}
                    </View>
                  </View>
                  <View style={styles.itemActionsRedesign}>
                    <TouchableOpacity
                      style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]} 
                      onPress={(e) => {
                        e.stopPropagation();
                        handleEditItem(item.id);
                      }}
                      activeOpacity={0.8}
                      accessibilityRole="button"
                      accessibilityLabel={`Edit item ${item.name}`}
                    >
                      <Ionicons name="pencil" size={20} color={theme.accent} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButtonRedesign, { backgroundColor: theme.inputBackground }]} 
                      onPress={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(item.id);
                      }}
                      activeOpacity={0.8}
                      accessibilityRole="button"
                      accessibilityLabel={`Delete item ${item.name}`}
                    >
                      <Ionicons name="trash" size={20} color={theme.accent} />
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </ScrollView>
      )}

      {/* Filter Modal */}
      <Modal
        visible={showFilterModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowFilterModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.card, borderRadius: 20, padding: 32, maxWidth: 400, width: '90%', alignItems: 'center', ...Platform.select({
            ios: {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 8,
            },
            android: {
              elevation: 8,
            },
            web: {
              boxShadow: '0 4px 24px 0 rgba(0,0,0,0.12)',
            },
          }) }}>
            {/* Close button */}
            <TouchableOpacity
              onPress={() => setShowFilterModal(false)}
              style={{ position: 'absolute', right: 16, top: 16, padding: 12, borderRadius: 24, backgroundColor: theme.inputBackground, zIndex: 2 }}
              accessibilityRole="button"
              accessibilityLabel="Close filter and sort modal"
            >
              <Ionicons name="close" size={28} color={theme.text} />
            </TouchableOpacity>
            {/* Title */}
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 12, marginTop: 8 }}>Filter & Sort</Text>
            {/* Divider */}
            <View style={dividerStyle} />
            {/* Subtitle */}
            <Text style={{ color: theme.text, fontWeight: '500', fontSize: 15, marginBottom: 18, textAlign: 'center' }}>Sort By</Text>
            {/* Sort buttons: Name first, then Most Recent */}
            <View style={{ flexDirection: 'row', gap: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 8 }}>
              {[ 'name', 'recent' ].map(option => (
                <ThemedButton
                  key={option}
                  title={option === 'name' ? 'Name' : 'Most Recent'}
                  onPress={() => handleSortSelect(option as SortOption)}
                  variant={sortBy === option ? 'primary' : 'secondary'}
                  style={{ minWidth: 110 }}
                  accessibilityLabel={option === 'recent' ? 'Sort by most recent' : 'Sort by name'}
                />
              ))}
            </View>

            {/* Divider */}
            <View style={dividerStyle} />

            {/* Filter By heading */}
            <Text style={{ color: theme.text, fontWeight: '500', fontSize: 15, marginBottom: 12, textAlign: 'center' }}>Filter By</Text>

            {/* Room selector button */}
            <TouchableOpacity
              onPress={() => setShowRoomPickerModal(true)}
              style={{
                width: '100%',
                borderWidth: 1,
                borderColor: theme.border,
                borderRadius: 8,
                marginBottom: 20,
                backgroundColor: theme.inputBackground,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingHorizontal: 16,
                paddingVertical: 12,
              }}
              accessibilityRole="button"
              accessibilityLabel="Select room to filter by"
            >
              <Text style={{ color: theme.text, fontSize: 16 }}>
                {selectedRoomFilter
                  ? rooms.find(r => r.id === selectedRoomFilter)?.name ?? 'Select Room'
                  : 'All Rooms'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={theme.text} />
            </TouchableOpacity>

            {/* Apply button */}
            <ThemedButton
              title="Apply"
              onPress={() => handleFilterSelect(selectedRoomFilter)}
              style={{ minWidth: 120 }}
              accessibilityLabel="Apply room filter"
            />

            {/* Room picker modal */}
            {renderRoomPickerModal()}
          </View>
        </View>
      </Modal>

      {/* Delete Modal */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}
      >
        <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
          <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 12 }}>Delete Item</Text>
            <Text style={[styles.deleteModalText, { color: theme.text }]}>Are you sure you want to delete this item?{"\n\n"}This will also remove any images, receipts (if not used by other items) and warranty documents for this item.</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 12, width: '100%' }}>
              <ThemedButton
                title="Cancel"
                variant="secondary"
                onPress={() => setShowDeleteModal(false)}
                style={{ flex: 1, minWidth: 140 }}
              />
              <ThemedButton
                title="Delete"
                onPress={confirmDelete}
                style={{ flex: 1, minWidth: 140 }}
              />
            </View>
          </View>
        </View>
      </Modal>

      {showToast && (
        <Animated.View
          style={[
            styles.toast,
            {
              backgroundColor: theme.accent,
              opacity: fadeAnim,
            },
          ]}
        >
          <Text style={[styles.toastText, { color: theme.buttonText }]}>{toastMessage}</Text>
        </Animated.View>
      )}
    </SafeAreaView>
  );
} 